import { lazy, Suspense, type ReactNode } from "react";
import {
  Routes,
  Route,
  Outlet,
  useNavigate,
  useParams,
  useLocation,
  matchPath,
  Navigate,
} from "react-router-dom";
import { ErrorPage } from "@safers/ui/templates";
import {
  ProtectedRouter,
  useAuthStore,
  selectUser,
  canAccessSite,
  canAccessMenuSite,
  hasMenu,
  type MenuKey,
} from "@safers/auth";
import { Spinner } from "@safers/ui/atoms";

import { LoginPage } from "@/pages";
import { MobileShell } from "@/components/mobile/MobileShell";
import { AREA_ROOT, HOME_PATH, LEGACY_REDIRECTS, LOGIN_PATH, SITE_PARAM } from "@/routes/paths";
import { useSignOut } from "@/hooks/useSignOut";
import { AreaEntry } from "@/routes/AreaEntry";

const ControlLayout = lazy(() =>
  import("@/routes/ControlLayout").then((m) => ({ default: m.ControlLayout }))
);
const HealthPage = lazy(() => import("@/pages/health").then((m) => ({ default: m.HealthPage })));
const HealthDashboardPage = lazy(() =>
  import("@/pages/health/dashboard").then((m) => ({ default: m.HealthDashboardPage }))
);
const SafetyReportPage = lazy(() =>
  import("@/pages/safety-report").then((m) => ({ default: m.SafetyReportPage }))
);
const CCTVAIPage = lazy(() => import("@/pages/cctv-ai").then((m) => ({ default: m.CCTVAIPage })));
const CCTVPlaybackPage = lazy(() =>
  import("@/pages/cctv-playback").then((m) => ({ default: m.CCTVPlaybackPage }))
);
const MePage = lazy(() => import("@/pages/me").then((m) => ({ default: m.MePage })));

function LazyFallback() {
  return (
    <div className="flex h-screen w-screen items-center justify-center">
      <Spinner size="lg" />
    </div>
  );
}

function NotFoundPage() {
  const navigate = useNavigate();
  return (
    <ErrorPage
      variant="404"
      primaryAction={{ label: "홈으로", onClick: () => navigate(HOME_PATH) }}
    />
  );
}

function ForbiddenPage() {
  const navigate = useNavigate();
  const handleLogout = useSignOut();
  return (
    <ErrorPage
      variant="403"
      primaryAction={{ label: "홈으로", onClick: () => navigate(HOME_PATH) }}
      secondaryAction={{ label: "로그아웃", onClick: handleLogout }}
    />
  );
}

function LegacyRedirect() {
  const params = useParams();
  const location = useLocation();
  const siteId = params[SITE_PARAM];
  const rule = LEGACY_REDIRECTS.find((r) => matchPath(r.from, location.pathname));
  if (!rule || !siteId) return <Navigate to={HOME_PATH} replace />;
  return <Navigate to={`${rule.to(siteId)}${location.search}${location.hash}`} replace />;
}

function SiteGate({ menu, children }: { menu?: MenuKey; children: ReactNode }) {
  const params = useParams();
  const user = useAuthStore(selectUser);
  const siteId = params[SITE_PARAM];
  if (siteId != null && !canAccessSite(user, siteId)) {
    return <Navigate to="/403" replace />;
  }
  if (siteId != null && menu && !canAccessMenuSite(user, siteId, menu)) {
    return <Navigate to="/403" replace />;
  }
  return <>{children}</>;
}

function MenuGate({ menu, children }: { menu: MenuKey; children: ReactNode }) {
  const user = useAuthStore(selectUser);
  if (!hasMenu(user, menu)) {
    return <Navigate to="/403" replace />;
  }
  return <>{children}</>;
}

function AuthLayout() {
  return (
    <ProtectedRouter
      fallback={
        <div className="flex h-screen w-screen items-center justify-center">
          <Spinner size="lg" />
        </div>
      }
    >
      <Outlet />
    </ProtectedRouter>
  );
}

export function AppRoutes() {
  return (
    <Routes>
      <Route path={LOGIN_PATH} element={<LoginPage />} />
      <Route path="/403" element={<ForbiddenPage />} />
      <Route path="/404" element={<NotFoundPage />} />
      <Route element={<AuthLayout />}>
        <Route element={<MobileShell />}>
          <Route
            element={
              <Suspense fallback={<LazyFallback />}>
                <ControlLayout />
              </Suspense>
            }
          >
            <Route path={AREA_ROOT.control} element={null} />
            <Route path={`${AREA_ROOT.control}/site/:${SITE_PARAM}`} element={null} />
          </Route>
          <Route
            path={AREA_ROOT.health}
            element={
              <MenuGate menu="HEALTH">
                <AreaEntry area="health">
                  <Suspense fallback={<LazyFallback />}>
                    <HealthPage />
                  </Suspense>
                </AreaEntry>
              </MenuGate>
            }
          />
          <Route
            path={`${AREA_ROOT.health}/site/:${SITE_PARAM}`}
            element={
              <SiteGate menu="HEALTH">
                <Suspense fallback={<LazyFallback />}>
                  <HealthDashboardPage />
                </Suspense>
              </SiteGate>
            }
          />
          <Route
            path={`${AREA_ROOT.health}/site/:${SITE_PARAM}/records`}
            element={
              <SiteGate menu="HEALTH">
                <Suspense fallback={<LazyFallback />}>
                  <SafetyReportPage />
                </Suspense>
              </SiteGate>
            }
          />
          <Route
            path={AREA_ROOT.cctv}
            element={
              <MenuGate menu="CCTV">
                <AreaEntry area="cctv" />
              </MenuGate>
            }
          />
          <Route
            path={`${AREA_ROOT.cctv}/site/:${SITE_PARAM}`}
            element={
              <SiteGate menu="CCTV">
                <Suspense fallback={<LazyFallback />}>
                  <CCTVAIPage />
                </Suspense>
              </SiteGate>
            }
          />
          <Route
            path={`${AREA_ROOT.cctv}/site/:${SITE_PARAM}/playback`}
            element={
              <SiteGate menu="CCTV">
                <Suspense fallback={<LazyFallback />}>
                  <CCTVPlaybackPage />
                </Suspense>
              </SiteGate>
            }
          />
          <Route
            path="me"
            element={
              <Suspense fallback={<LazyFallback />}>
                <MePage />
              </Suspense>
            }
          />
          <Route index element={<Navigate to={HOME_PATH} replace />} />
          <Route path="dashboard" element={<Navigate to={HOME_PATH} replace />} />
          {LEGACY_REDIRECTS.map(({ from }) => (
            <Route key={from} path={from} element={<LegacyRedirect />} />
          ))}
        </Route>
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
